import type {
  CoachingGoal,
  CoachingGoalMilestone,
} from '@efdigital/ll-addon-data';
import GoalPreviewRow from '@efdigital/ll-xceed-v2/components/coaching/goal-preview-row.gts';
import { milestonesForGoal } from '@efdigital/ll-xceed-v2/utils/coaching/goal-children';
import {
  type GoalProgressSummary,
  goalProgressSummary,
} from '@efdigital/ll-xceed-v2/utils/coaching/goal-progress';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

const DONUT_RADIUS = 42;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;
const ACTIVE_PREVIEW_LIMIT = 3;
const EMPTY_GOALS: CoachingGoal[] = [];

interface GoalTrackerSignature {
  Args: {
    goals: readonly CoachingGoal[] | null | undefined;
    milestones: readonly CoachingGoalMilestone[];
    /** When a coaching plan exists, goals can be added. */
    canAddGoal?: boolean;
    onAddGoals?: () => void;
    programId?: string;
    cohortId?: string;
    /** Dashboard preview of active goals. Goals page uses the overall progress card. */
    showActivePreview?: boolean;
    /** Coach copy talks about the coachee; coachee copy is first person. */
    role?: 'coach' | 'coachee';
  };
  Element: HTMLDivElement;
}

export default class GoalTracker extends Component<GoalTrackerSignature> {
  readonly donutRadius = DONUT_RADIUS;
  readonly donutCircumference = DONUT_CIRCUMFERENCE;

  get summary(): GoalProgressSummary {
    return goalProgressSummary(this.args.goals, (goalId) =>
      milestonesForGoal(this.args.milestones, goalId)
    );
  }

  get show(): boolean {
    return this.summary.totalGoals > 0 || Boolean(this.args.canAddGoal);
  }

  get averagePercent(): number {
    return this.summary.averagePercent;
  }

  get progressDashOffset(): number {
    return DONUT_CIRCUMFERENCE * (1 - this.averagePercent / 100);
  }

  get goalsLabel(): string {
    const { goalsComplete, totalGoals } = this.summary;

    if (totalGoals === 0) {
      return 'No goals yet';
    }

    return `${goalsComplete} of ${totalGoals} goal${totalGoals === 1 ? '' : 's'} complete`;
  }

  get milestonesLabel(): string {
    const { milestonesDone, totalMilestones } = this.summary;

    if (totalMilestones === 0) {
      return 'No milestones yet';
    }

    return `${milestonesDone} of ${totalMilestones} milestone${totalMilestones === 1 ? '' : 's'} done`;
  }

  get progressDescription(): string {
    if (this.args.role === 'coach') {
      return "The ring shows this coachee's milestone progress. A goal only counts as complete when they mark the overall goal completed.";
    }

    return 'The ring shows your milestone progress. A goal only counts as complete when you mark the overall goal completed.';
  }

  @cached
  get activeGoalsPreview(): readonly CoachingGoal[] {
    const goals = this.args.goals;

    if (!goals?.length) {
      return EMPTY_GOALS;
    }

    const preview: CoachingGoal[] = [];

    for (const goal of goals) {
      if ((goal.status ?? 'active') === 'active') {
        preview.push(goal);

        if (preview.length === ACTIVE_PREVIEW_LIMIT) {
          break;
        }
      }
    }

    return preview.length ? preview : EMPTY_GOALS;
  }

  get showViewAll(): boolean {
    return (
      Boolean(this.args.showActivePreview) &&
      Boolean(this.args.programId && this.args.cohortId)
    );
  }

  milestoneDetail = (goal: CoachingGoal): string => {
    const milestones = milestonesForGoal(this.args.milestones, String(goal.id));

    if (milestones.length === 0) {
      return 'No milestones yet';
    }

    const done = milestones.filter((milestone) => milestone.completed).length;

    if (done === milestones.length) {
      return `All ${milestones.length} complete`;
    }

    return `${done} of ${milestones.length} complete`;
  };

  addGoals = () => {
    this.args.onAddGoals?.();
  };

  <template>
    {{#if this.show}}
      <div class="row g-3 mb-4 align-items-stretch" ...attributes>
        <div class="col-12 col-lg-5 col-xl-4 d-flex">
          <div
            class="card border shadow-sm rounded-4 w-100 overflow-hidden"
            data-test-goal-tracker
          >
            <div
              class="card-body d-flex flex-column text-center p-3 p-md-4 px-md-5 px-lg-5"
            >
              <div class="position-relative d-flex justify-content-center mb-3">
                <span class="visually-hidden">Overall goal progress:
                  {{this.averagePercent}}
                  percent.
                  {{this.goalsLabel}}.
                  {{this.milestonesLabel}}.</span>
                <svg
                  width="120"
                  height="120"
                  viewBox="0 0 100 100"
                  aria-hidden="true"
                  class="img-fluid"
                >
                  <circle
                    class="ll-chart-track"
                    cx="50"
                    cy="50"
                    r={{this.donutRadius}}
                    fill="none"
                    stroke="currentColor"
                    stroke-width="8"
                  />
                  <circle
                    class="text-primary"
                    cx="50"
                    cy="50"
                    r={{this.donutRadius}}
                    fill="none"
                    stroke="currentColor"
                    stroke-width="8"
                    stroke-dasharray={{this.donutCircumference}}
                    stroke-dashoffset={{this.progressDashOffset}}
                    stroke-linecap="round"
                    transform="rotate(-90 50 50)"
                  />
                </svg>
                {{! Arc length and the numeric label communicate progress;
                    colour is decorative and never encodes good/bad status. }}
                <span
                  class="position-absolute top-50 start-50 translate-middle fw-bold fs-4 mb-0 text-body-emphasis"
                  data-test-goal-tracker-percent
                >{{this.averagePercent}}%</span>
              </div>
              <p class="fw-bold mb-1 text-body-emphasis">{{this.goalsLabel}}</p>
              <p class="text-body-secondary mb-0">{{this.milestonesLabel}}</p>
              {{#if @showActivePreview}}
                {{#if @canAddGoal}}
                  <div class="mt-auto pt-3">
                    <button
                      type="button"
                      class="btn btn-primary"
                      {{on "click" this.addGoals}}
                    >
                      <iconify-icon inline icon="ph:plus" aria-hidden="true" />
                      Add goals
                    </button>
                  </div>
                {{/if}}
              {{/if}}
            </div>
          </div>
        </div>
        <div class="col-12 col-lg-7 col-xl-8 d-flex">
          {{#if @showActivePreview}}
            <div class="card border shadow-sm rounded-4 w-100 overflow-hidden">
              <div class="card-body d-flex flex-column h-100 p-3 p-md-4">
                <div
                  class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3"
                >
                  <h2 class="h5 fw-bold mb-0 text-body-emphasis">Active goals</h2>
                  {{#if this.showViewAll}}
                    <LinkTo
                      @route="coaching.coachee.cohort.goals"
                      class="btn btn-link link-primary text-decoration-none p-0 fw-semibold"
                    >View all<span class="visually-hidden">
                        goals</span></LinkTo>
                  {{/if}}
                </div>
                {{#if this.activeGoalsPreview.length}}
                  <div class="d-flex flex-column gap-2">
                    {{#each this.activeGoalsPreview key="id" as |goal|}}
                      <GoalPreviewRow
                        @goal={{goal}}
                        @detail={{this.milestoneDetail goal}}
                        @programId={{@programId}}
                      />
                    {{/each}}
                  </div>
                {{else}}
                  <p class="text-body-secondary mb-0">No active goals yet.</p>
                {{/if}}
              </div>
            </div>
          {{else}}
            <div class="card border shadow-sm rounded-4 w-100">
              <div class="card-body d-flex flex-column h-100 p-3 p-md-4">
                <h2 class="h5 fw-bold mb-2">Overall progress</h2>
                <p
                  class="text-body-secondary mb-0"
                >{{this.progressDescription}}</p>
                {{#if @canAddGoal}}
                  <div class="mt-auto pt-3">
                    <button
                      type="button"
                      class="btn btn-primary"
                      {{on "click" this.addGoals}}
                    >
                      <iconify-icon inline icon="ph:plus" aria-hidden="true" />
                      Add goals
                    </button>
                  </div>
                {{/if}}
              </div>
            </div>
          {{/if}}
        </div>
      </div>
    {{/if}}
  </template>
}
